import { useState } from 'react'
import { useBreadcrumb } from '../hooks/useBreadcrumb'
import toast from 'react-hot-toast'
import { FaSearch, FaCheckCircle,FaBoxOpen, FaClock, FaImage, FaBox, FaWeight, FaRuler } from 'react-icons/fa'
import { Card, Button, PageHeader } from '../components/common'
import { get } from '../services/api'
import { formatDateTime } from '../utils/datetime'

const statusMap = {
  not_stored: { label: '未入库', color: 'bg-gray-100 text-gray-700', icon: FaClock },
  not_in_warehouse: { label: '未入库', color: 'bg-gray-100 text-gray-700', icon: FaClock },
  pending: { label: '待验货', color: 'bg-yellow-100 text-yellow-800', icon: FaClock },
  inspected: { label: '已验货', color: 'bg-green-100 text-green-800', icon: FaCheckCircle },
}

const resultMap = {
  pass: { label: '合格', color: 'text-green-600' },
  fail: { label: '不合格', color: 'text-red-600' },
  damaged: { label: '破损', color: 'text-orange-600' },
}

export default function InspectionPage() {
  const breadcrumb = useBreadcrumb()
  const [searchCode, setSearchCode] = useState('')
  const [searchResult, setSearchResult] = useState(null)
  const [isSearching, setIsSearching] = useState(false)

  // 根据货物编号查询
  const handleSearch = async () => {
    if (!searchCode.trim()) {
      toast.error('请输入货物编号')
      return
    }
    setIsSearching(true)
    try {
      const result = await get('/blade-portal/inspection/query-by-code', { cargoCode: searchCode.trim() })
      setSearchResult(result)
    } catch (error) {
      toast.error(error.message || '未找到验货记录')
      setSearchResult(null)
    } finally {
      setIsSearching(false)
    }
  }

  // 解析照片JSON
  const parsePhotos = (photosJson) => {
    if (!photosJson) return []
    try {
      return JSON.parse(photosJson)
    } catch {
      return []
    }
  }

  return (
    <div>
      <PageHeader
        icon={FaSearch}
        title="验货查询"
        subtitle="查询货物验货状态和结果"
        breadcrumb={breadcrumb}
      />

      <div className="container py-8">
        {/* 搜索卡片 */}
        <Card className="mb-8">
          <Card.Body>
            <div className="max-w-2xl mx-auto">
              <h3 className="text-fluid-lg font-semibold mb-4 text-center">输入货物编号查询验货状态</h3>
              {/* 小屏竖排：并排时输入框只剩 128px，装不下提示文字 */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <div className="flex-1 min-w-0">
                  <input
                    type="text"
                    className="form-control sm:text-lg"
                    placeholder="请输入货物编号"
                    value={searchCode}
                    onChange={(e) => setSearchCode(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                </div>
                <Button size="lg" onClick={handleSearch} loading={isSearching} icon={<FaSearch />} className="w-full sm:w-auto shrink-0">
                  查询
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* 查询结果 */}
        {searchResult && (
          <Card>
            <Card.Header>
              <div className="flex items-center justify-between">
                <h4 className="font-semibold">验货信息</h4>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusMap[searchResult.inspection_status]?.color}`}>
                  {statusMap[searchResult.inspection_status]?.label || searchResult.inspection_status}
                </span>
              </div>
            </Card.Header>
            <Card.Body>
              <div className="grid md:grid-cols-2 gap-6">
                {/* 基本信息 */}
                <div className="space-y-4">
                  <h5 className="font-medium border-b pb-2">货物信息</h5>
                  <div className="grid grid-cols-fluid-sm gap-4">
                    <div>
                      <p className="text-sm text-gray-500">验货编号</p>
                      <p className="font-medium">{searchResult.inspection_code}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">货物编号</p>
                      <p className="font-medium">{searchResult.cargo_code}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">货物名称</p>
                      <p className="font-medium">{searchResult.cargo_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">重量</p>
                      <p className="font-medium flex items-center gap-1">
                        <FaWeight className="text-gray-400" />
                        {searchResult.weight} KG
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">体积</p>
                      <p className="font-medium flex items-center gap-1">
                        <FaRuler className="text-gray-400" />
                        {searchResult.volume ? `${searchResult.volume} CBM` : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">尺寸 (长×宽×高)</p>
                      <p className="font-medium">
                        {searchResult.length && searchResult.width && searchResult.height 
                          ? `${searchResult.length}×${searchResult.width}×${searchResult.height} cm`
                          : '-'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* 验货结果 */}
                <div className="space-y-4">
                  <h5 className="font-medium border-b pb-2">验货结果</h5>
                  {searchResult.inspection_status === 'inspected' ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-fluid-sm gap-4">
                        <div>
                          <p className="text-sm text-gray-500">验货结果</p>
                          <p className={`font-semibold ${resultMap[searchResult.inspection_result]?.color || 'text-gray-600'}`}>
                            {resultMap[searchResult.inspection_result]?.label || searchResult.inspection_result || '-'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">验货时间</p>
                          <p className="font-medium">
                            {formatDateTime(searchResult.inspected_time, 'YYYY-MM-DD HH:mm')}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">验货人</p>
                          <p className="font-medium">{searchResult.inspector_name || '-'}</p>
                        </div>
                      </div>
                      {searchResult.inspection_remark && (
                        <div>
                          <p className="text-sm text-gray-500">验货备注</p>
                          <p className="font-medium bg-gray-50 p-3 rounded-lg">{searchResult.inspection_remark}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <FaClock className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
                      <p className="text-gray-500">货物尚未验货，请耐心等待</p>
                    </div>
                  )}
                </div>
              </div>

              {/* 验货照片 */}
              {searchResult.inspection_status === 'inspected' && parsePhotos(searchResult.photos).length > 0 && (
                <div className="mt-6 pt-6 border-t">
                  <h5 className="font-medium mb-4 flex items-center gap-2">
                    <FaImage className="text-gray-400" />
                    验货照片
                  </h5>
                  <div className="grid grid-cols-fluid-xs gap-4">
                    {parsePhotos(searchResult.photos).map((photo, index) => (
                      <div key={index} className="aspect-square rounded-lg overflow-hidden border">
                        <img 
                          src={photo} 
                          alt={`验货照片 ${index + 1}`}
                          className="w-full h-full object-cover hover:scale-105 transition-transform cursor-pointer"
                          onClick={() => window.open(photo, '_blank')}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        )}

        {/* 验货状态说明 */}
        <Card className="mt-8">
          <Card.Header>
            <h5 className="font-semibold">验货流程说明</h5>
          </Card.Header>
          <Card.Body>
            <div className="grid grid-cols-fluid-lg gap-6">
              <div className="text-center p-4">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaBoxOpen className="w-8 h-8 text-purple-500" />
                </div>
                <h6 className="font-medium mb-2">1. 未入库</h6>
                <p className="text-sm text-gray-500">货物尚未入库时显示“未入库”状态</p>
              </div>
              <div className="text-center p-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaBox className="w-8 h-8 text-blue-500" />
                </div>
                <h6 className="font-medium mb-2">2. 货物入库</h6>
                <p className="text-sm text-gray-500">货物到达仓库后，系统自动生成验货任务</p>
              </div>
              <div className="text-center p-4">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaClock className="w-8 h-8 text-yellow-500" />
                </div>
                <h6 className="font-medium mb-2">3. 等待验货</h6>
                <p className="text-sm text-gray-500">仓库工作人员按顺序对货物进行验货检查</p>
              </div>
              <div className="text-center p-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaCheckCircle className="w-8 h-8 text-green-500" />
                </div>
                <h6 className="font-medium mb-2">4. 验货完成</h6>
                <p className="text-sm text-gray-500">验货完成后可查看验货结果和照片</p>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>
    </div>
  )
}
